/* eslint-disable @typescript-eslint/no-explicit-any */
import { TypedOmit } from '@client/new/helpers/types';
import { Input, InputProps } from '@design-system/components/input/input';
import { faDollar, faPercent } from '@fortawesome/pro-regular-svg-icons';
import { Col } from '@design-system/components/grid/grid';
import { ErrorMessage } from '@client/new/components/Label';
import { forwardRef, useState } from 'react';

type NumberInputProps = TypedOmit<InputProps, 'value' | 'onChange'> & {
  width?: number;
  value: number | null | undefined;
  onChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  defaultValue?: number;
  min?: number | string;
  max?: number | string;
  endCta?: string;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, defaultValue, onChange, width, min, max, ...p }, ref) => {
    const [isUserEditing, setIsUserEditing] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState<string>('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsUserEditing(true);
      setInputValue(e.target.value);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (p.onBlur) p.onBlur(e);
      setIsUserEditing(false);
      setIsFocused(false);

      // todo JB unit tests
      if (!inputValue) return;
      const input = inputValue.replace(',', '.');
      const target = { name: p.name, value: defaultValue || min || 0 };
      if (input === '') onChange({ target } as any);
      else {
        let parsedValue = parseFloat(input);
        if (min !== undefined)
          parsedValue = Math.max(parsedValue, parseInt(min + '', 10));
        if (max !== undefined)
          parsedValue = Math.min(parsedValue, parseInt(max + '', 10));
        target.value = parsedValue;
        if (!isNaN(parsedValue)) onChange({ target } as any);
      }
    };

    return (
      <Col style={width ? { width: width + 'px' } : {}}>
        <Input
          {...p}
          value={
            isUserEditing && isFocused
              ? inputValue
              : value === null
                ? defaultValue
                : value + ''
          }
          ref={ref}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          type="number"
          hideError
        />
        <div style={{ marginTop: '0' }}>
          <ErrorMessage content={p.error} />
        </div>
      </Col>
    );
  },
);

export const PercentInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ ...p }, ref) => (
    <NumberInput
      step="0.01"
      max={100}
      min={0}
      endIcon={{ icon: faPercent }}
      ref={ref}
      {...p}
    />
  ),
);

export const DollarsInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ ...p }, ref) => (
    <NumberInput
      step="0.01"
      {...p}
      min={0}
      startIcon={{ icon: faDollar }}
      ref={ref}
    />
  ),
);
